import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Select, DatePicker, Switch, Radio, InputNumber, Input, Space, Button, Spin, Collapse, Tag, message, Tooltip } from 'antd';
import dayjs from 'dayjs';
import adminProductService from '../../services/adminProductService';
import { CYCLE_LABEL, TYPE_LABEL, PLAN_CHANGE_TYPES } from './adminProductMeta';

/**
 * 后台改订阅：套餐（赠送 / 扣余额）、周期、到期日、自动续费；价格与备注折叠在「更多」里。
 * 用在：用户详情「产品」、订阅管理、CDN 域名列表与详情。
 */
const AdminSubscriptionEditModal = ({ open, subscriptionId, onClose, onSaved }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    const productId = Form.useWatch('product_id', form);

    useEffect(() => {
        if (!open || !subscriptionId) return;
        let alive = true;
        setLoading(true);
        setData(null);
        adminProductService.planOptions(subscriptionId).then((r) => {
            if (!alive) return;
            if (!r?.success) { message.error(r?.error || '加载失败'); return; }
            const s = r.data.subscription;
            setData(r.data);
            form.setFieldsValue({
                product_id: s.product_id,
                billing_cycle: s.billing_cycle,
                mode: 'grant',
                expiry_date: s.expiry_date ? dayjs(s.expiry_date) : null,
                auto_renew: !!s.auto_renew,
                price: s.price != null ? Number(s.price) : null,
                notes: s.notes || '',
            });
        }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, subscriptionId, form]);

    const sub = data?.subscription;
    const plan = useMemo(() => (data?.plans || []).find((p) => p.id === productId), [data, productId]);
    const planChanged = sub && productId && productId !== sub.product_id;
    const canChangePlan = !!data?.plan_change && PLAN_CHANGE_TYPES.includes(sub?.product_type_code);

    const shiftExpiry = (n, unit) => {
        const cur = form.getFieldValue('expiry_date');
        const base = cur && cur.isAfter(dayjs()) ? cur : dayjs();
        form.setFieldsValue({ expiry_date: base.add(n, unit) });
    };

    const submit = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            if (planChanged) {
                const r = await adminProductService.changePlan(sub.id, v.product_id, v.mode, v.billing_cycle !== sub.billing_cycle ? v.billing_cycle : null);
                if (!r?.success) { message.error(r?.error || '改套餐失败'); return; }
                if (r.mode === 'charge') message.success(`已扣余额并生成升级单 #${r.invoice_id}`);
                else if (r.queued) message.success('套餐已提交，开通队列处理中');
            }
            const upd = {};
            const exp = v.expiry_date ? v.expiry_date.format('YYYY-MM-DD HH:mm:ss') : null;
            // 改套餐（免费→付费）时后端已按周期算好到期日，除非管理员手动改了
            const origExp = sub.expiry_date ? dayjs(sub.expiry_date).format('YYYY-MM-DD HH:mm:ss') : null;
            if (exp && exp !== origExp) upd.expiry_date = exp;
            if (!planChanged && v.billing_cycle && v.billing_cycle !== sub.billing_cycle) upd.billing_cycle = v.billing_cycle;
            if (v.auto_renew !== !!sub.auto_renew) upd.auto_renew = v.auto_renew ? 1 : 0;
            if (v.price != null && Number(v.price) !== Number(sub.price)) upd.price = v.price;
            if ((v.notes || '') !== (sub.notes || '')) upd.notes = v.notes || '';
            if (Object.keys(upd).length) {
                const r = await adminProductService.update(sub.id, upd);
                if (!r?.success) { message.error(r?.error || '保存失败'); return; }
            }
            message.success('已保存');
            onSaved && onSaved();
            onClose && onClose();
        } finally {
            setSaving(false);
        }
    };

    const cycles = (plan?.pricing || []).map((p) => ({ value: p.billing_cycle, label: `${CYCLE_LABEL[p.billing_cycle] || p.billing_cycle} · ${p.price} ${p.currency || ''}` }));

    return (
        <Modal
            title={sub ? <Space>{`订阅 #${sub.id}`}<Tag>{TYPE_LABEL[sub.product_type_code] || sub.product_type_code}</Tag>{data?.owner && <Tag color="gold">业主</Tag>}</Space> : '订阅'}
            open={open}
            onCancel={onClose}
            onOk={submit}
            confirmLoading={saving}
            okText="保存"
            cancelText="取消"
            destroyOnClose
        >
            <Spin spinning={loading}>
                <Form form={form} layout="vertical">
                    <Form.Item name="product_id" label="套餐">
                        <Select
                            disabled={!canChangePlan}
                            options={(data?.plans || []).map((p) => ({ value: p.id, label: p.status === 'active' ? p.name : `${p.name}（已下架）` }))}
                            onChange={(id) => {
                                const p = (data?.plans || []).find((x) => x.id === id);
                                const cur = form.getFieldValue('billing_cycle');
                                if (p && !p.pricing.some((x) => x.billing_cycle === cur)) form.setFieldsValue({ billing_cycle: p.pricing[0]?.billing_cycle });
                            }}
                        />
                    </Form.Item>
                    {planChanged && (
                        <Form.Item name="mode" label="费用" initialValue="grant">
                            <Radio.Group disabled={data?.owner}>
                                <Radio value="grant">赠送</Radio>
                                <Tooltip title="按剩余天数折算差价，从用户余额扣（仅升级）"><Radio value="charge">扣余额</Radio></Tooltip>
                            </Radio.Group>
                        </Form.Item>
                    )}
                    <Form.Item name="billing_cycle" label="计费周期">
                        <Select options={cycles} />
                    </Form.Item>
                    <Form.Item label="到期时间" style={{ marginBottom: 8 }}>
                        <Space wrap>
                            <Form.Item name="expiry_date" noStyle>
                                <DatePicker showTime format="YYYY-MM-DD HH:mm" disabled={sub?.product_type_code === 'domain'} />
                            </Form.Item>
                            <Button size="small" onClick={() => shiftExpiry(1, 'month')}>+1 月</Button>
                            <Button size="small" onClick={() => shiftExpiry(1, 'year')}>+1 年</Button>
                        </Space>
                    </Form.Item>
                    <Form.Item name="auto_renew" label="自动续费" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Collapse ghost size="small" items={[{
                        key: 'more',
                        label: '更多',
                        children: (
                            <>
                                <Form.Item name="price" label="订阅价格">
                                    <InputNumber min={0} step={0.01} style={{ width: 160 }} />
                                </Form.Item>
                                <Form.Item name="notes" label="备注">
                                    <Input.TextArea rows={2} maxLength={2000} />
                                </Form.Item>
                            </>
                        ),
                    }]} />
                </Form>
            </Spin>
        </Modal>
    );
};

export default AdminSubscriptionEditModal;
